import {
    Alert,
    AlertIcon,
    Box,
    Button,
    FormLabel,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { ApiError } from "../../../apiClient";
import { translate } from "../../../i18n";
import { useSetUserPassword } from "../hooks/useSetUserPassword";

/** Mirrors the API DTO's `@MinLength(6)` — Firebase's own floor. */
export const MIN_PASSWORD_LENGTH = 6;

/**
 * Maps the API's error messages to a translated line. The endpoint answers
 * with stable, non-leaking strings (`USER_IS_GUEST`, `USER_HAS_NO_ACCOUNT`,
 * `PASSWORD_TOO_SHORT`) rather than prose, so the operator gets an actionable
 * reason instead of a generic failure toast.
 */
function messageForError(error: unknown): string {
    const apiError = error instanceof ApiError ? error : undefined;
    switch (apiError?.message) {
        case "USER_IS_GUEST":
            return translate("user.password_error_guest");
        case "USER_HAS_NO_ACCOUNT":
            return translate("user.password_error_no_account");
        case "PASSWORD_TOO_SHORT":
        case "PASSWORD_TOO_LONG":
            return translate("user.password_too_short");
        default:
            if (apiError?.status === 403) return translate("user.password_error_forbidden");
            return translate("user.password_update_failed");
    }
}

export default function ChangeUserPasswordModal({
    userId,
    userEmail,
    isOpen,
    onClose,
}: {
    userId: string;
    userEmail?: string;
    isOpen: boolean;
    onClose: () => void;
}) {
    const toast = useToast();
    const { mutateAsync: setUserPassword, isLoading } = useSetUserPassword(userId);
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [isRevealed, setIsRevealed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Never leave a typed credential sitting in component state once the modal
    // is dismissed — reopening it must start blank.
    useEffect(() => {
        if (!isOpen) {
            setPassword("");
            setConfirmation("");
            setIsRevealed(false);
            setError(null);
        }
    }, [isOpen]);

    const isTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    const isMismatched = confirmation.length > 0 && password !== confirmation;
    const canSubmit =
        password.length >= MIN_PASSWORD_LENGTH && password === confirmation && !isLoading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setError(null);
        try {
            const result = await setUserPassword({ newPassword: password });
            toast({
                title: result?.created
                    ? translate("user.password_account_created")
                    : translate("user.password_updated_successfully"),
                description: translate("user.password_sessions_revoked"),
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            onClose();
        } catch (caught) {
            setError(messageForError(caught));
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate("user.change_password")}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Text fontSize="sm" color="gray.500">
                            {userEmail
                                ? `${translate("user.change_password_description")} (${userEmail})`
                                : translate("user.change_password_description")}
                        </Text>
                        <Box>
                            <FormLabel>{translate("user.new_password")}</FormLabel>
                            <InputGroup>
                                <Input
                                    type={isRevealed ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={translate("user.new_password_placeholder")}
                                    autoComplete="new-password"
                                />
                                <InputRightElement>
                                    <IconButton
                                        size="sm"
                                        variant="ghost"
                                        aria-label={
                                            isRevealed
                                                ? translate("user.hide_password")
                                                : translate("user.show_password")
                                        }
                                        icon={isRevealed ? <ViewOffIcon /> : <ViewIcon />}
                                        onClick={() => setIsRevealed((current) => !current)}
                                    />
                                </InputRightElement>
                            </InputGroup>
                            {isTooShort && (
                                <Text fontSize="sm" color="red.400" mt={1}>
                                    {translate("user.password_too_short")}
                                </Text>
                            )}
                        </Box>
                        <Box>
                            <FormLabel>{translate("user.confirm_password")}</FormLabel>
                            <Input
                                type={isRevealed ? "text" : "password"}
                                value={confirmation}
                                onChange={(e) => setConfirmation(e.target.value)}
                                placeholder={translate("user.confirm_password_placeholder")}
                                autoComplete="new-password"
                            />
                            {isMismatched && (
                                <Text fontSize="sm" color="red.400" mt={1}>
                                    {translate("user.passwords_do_not_match")}
                                </Text>
                            )}
                        </Box>
                        {error && (
                            <Alert status="error" borderRadius="md" fontSize="sm">
                                <AlertIcon />
                                {error}
                            </Alert>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3} variant="ghost">
                        {translate("common.cancel")}
                    </Button>
                    <Button
                        colorScheme="brand"
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        isDisabled={!canSubmit}
                    >
                        {translate("user.set_password")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
